import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Box, Button, Typography, CircularProgress, Paper, useTheme } from '@mui/material';
import {
  BarVisualizer,
  LiveKitRoom,
  RoomAudioRenderer,
  TrackToggle,
  useTranscriptions,
  useVoiceAssistant
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useGetAgent } from '@/apis/business/agent/hook';
import { getLiveKitToken, getPublicAgentWidgetConfig, type PublicAgentWidgetConfig } from '@/apis/business/agent/api';
import { useGetOrganizations } from '@/apis/agency/organizations/hook';
import TestAudioIcon from '@/assets/icons/TestAudioIcon';
import Cookies from 'js-cookie';
import { MuiTelInput, matchIsValidTel, AsYouType } from 'mui-tel-input';
import { MdMic } from 'react-icons/md';
import {
  PreviewCard,
  PreviewContinueButton,
  PreviewFloatingClose,
  PreviewFloatingVoiceBar,
  PreviewKickFooter,
  PreviewMinimizedVoiceStrip,
  PreviewScreenHeader,
  PreviewSmsDisclaimer,
  PREVIEW_BLUE
} from '@/components/Common/PreviewLink/PreviewLinkUi';
import TelInput from './TelInput';
import { useForm, useWatch } from 'react-hook-form';
import PreviewPhoneHero from '@/assets/icons/PreviewPhoneHero';

interface LiveKitAudioTestProps {
  isInDrawer?: boolean;
  agentToken?: string;
  isPreviewLink?: boolean;
  onPreviewExitToSwitch?: () => void;
  /** When true (PromptBuilder Test Workflow), marks the session as a test run. */
  testMode?: boolean;
  /** Run config against this agent version (draft or published). */
  agentVersionId?: string | null;
}

const generateWebUUID = () => {
  const webUUID = Cookies.get('web_uuid');
  if (!webUUID) {
    return generateAndSetWebUUID();
  }
  return webUUID;
};

const generateAndSetWebUUID = () => {
  const webUUID = crypto.randomUUID();
  Cookies.set('web_uuid', webUUID, { expires: 3 });
  return webUUID;
};

export default function LiveKitAudioTest({
  isInDrawer = false,
  agentToken: agentTokenProp,
  isPreviewLink = false,
  onPreviewExitToSwitch,
  testMode = false,
  agentVersionId
}: LiveKitAudioTestProps = {}) {
  return (
    <AudioTestContent
      isInDrawer={isInDrawer}
      agentToken={agentTokenProp}
      isPreviewLink={isPreviewLink}
      onPreviewExitToSwitch={onPreviewExitToSwitch}
      testMode={testMode}
      agentVersionId={agentVersionId}
    />
  );
}

function AudioTestContent({
  isInDrawer = false,
  agentToken: agentTokenProp,
  isPreviewLink = false,
  onPreviewExitToSwitch,
  testMode = false,
  agentVersionId
}: {
  isInDrawer?: boolean;
  agentToken?: string;
  isPreviewLink?: boolean;
  onPreviewExitToSwitch?: () => void;
  testMode?: boolean;
  agentVersionId?: string | null;
}) {
  const { data: organization } = useGetOrganizations();
  const [connectionDetails, setConnectionDetails] = useState<{
    access_token: string;
    websocket_url: string;
  } | null>(null);
  // Personalized link: empty. Internal test (Talk / TestingSidebar): keep env default.
  const defaultTestPhone = isPreviewLink ? '' : import.meta.env.VITE_APP_DEFAULT_PREVIEW_PHONE || '';
  const [phone, setPhone] = useState(defaultTestPhone);
  const [phoneOnly, setPhonePhoneOnly] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [showPreviewSmsDisclaimer, setShowPreviewSmsDisclaimer] = useState(false);
  const { control: previewPhoneControl } = useForm<{ previewPhone: string }>({
    defaultValues: {
      previewPhone: ''
    }
  });
  const previewPhone = useWatch({ control: previewPhoneControl, name: 'previewPhone' });
  const [isFetchingToken, setIsFetchingToken] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const isValid = matchIsValidTel(phone);

  const { data: agent } = useGetAgent();
  const effectiveToken = agentTokenProp ?? agent?.agent_token;

  const { data: previewAgent } = useQuery({
    queryKey: ['publicWidgetConfig', agentTokenProp],
    queryFn: () => getPublicAgentWidgetConfig(agentTokenProp!),
    enabled: Boolean(isPreviewLink && agentTokenProp)
  });

  const handleConnect = useCallback(async () => {
    if (!effectiveToken) return;

    try {
      setIsFetchingToken(true);
      const webUUID = generateWebUUID();
      const result = await getLiveKitToken(effectiveToken, {
        web_uuid: webUUID,
        channel: 'web_call',
        interaction_type: 'call',
        source_info: phoneOnly,
        country_code: countryCode,
        ...(isPreviewLink ? { personalized_link: true } : {}),
        ...(testMode ? { test: true } : {}),
        ...(agentVersionId ? { agent_version_id: agentVersionId } : {})
      });

      if (result) {
        setConnectionDetails(result);
      }
    } catch (err) {
      console.error('Failed to get LiveKit token:', err);
    } finally {
      setIsFetchingToken(false);
    }
  }, [effectiveToken, phoneOnly, countryCode, isPreviewLink, testMode, agentVersionId]);

  const handleDisconnect = useCallback(() => {
    setConnectionDetails(null);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow paste and other shortcuts (Cmd/Ctrl+V, etc.)
    if (e.metaKey || e.ctrlKey || e.altKey) {
      setIsEnabled(true);
      return;
    }

    const isNumber = /^[0-9]$/.test(e.key);

    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];

    if (!isEnabled) {
      if (isNumber) {
        setIsEnabled(true);
      } else if (!allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  useEffect(() => {
    // Personalized link: sync from empty TelInput form. Test: parse env default once (and on phone edits via onChange).
    const normalizedPhone = isPreviewLink ? (previewPhone ?? '') : defaultTestPhone;
    if (isPreviewLink) {
      setPhone(normalizedPhone);
    }

    const digitsOnly = normalizedPhone.replace(/\D/g, '');
    if (!digitsOnly) {
      if (isPreviewLink) {
        setPhonePhoneOnly('');
        setCountryCode('');
      }
      return;
    }

    if (normalizedPhone.startsWith('+')) {
      const asYouType = new AsYouType();
      asYouType.input(normalizedPhone);
      const parsed = asYouType.getNumber();
      const callingCode = parsed?.countryCallingCode ?? asYouType.getCallingCode();

      setCountryCode(callingCode ? `+${callingCode}` : '');
      setPhonePhoneOnly(parsed?.nationalNumber ?? (callingCode ? digitsOnly.slice(callingCode.length) : digitsOnly));
      return;
    }

    setCountryCode('');
    setPhonePhoneOnly(digitsOnly);
  }, [previewPhone, isPreviewLink, defaultTestPhone]);

  if (isFetchingToken && isPreviewLink) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <PreviewCard sx={{ alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
          <CircularProgress size={40} sx={{ color: PREVIEW_BLUE }} />
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Connecting...
          </Typography>
        </PreviewCard>
      </Box>
    );
  }

  if (isFetchingToken && !isPreviewLink) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        gap={2}
        p={3}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: isInDrawer ? 'calc(100vh - 180px)' : '100%'
        }}
      >
        <CircularProgress size={40} />
        <Typography variant='body2' color='text.secondary'>
          Connecting...
        </Typography>
      </Box>
    );
  }

  if (connectionDetails) {
    return (
      <LiveKitRoom
        token={connectionDetails?.access_token}
        serverUrl={connectionDetails?.websocket_url}
        connect={!!connectionDetails}
        audio={true}
        video={false}
        style={{ width: '100%', height: '100%' }}
        options={{
          adaptiveStream: true,
          dynacast: true,
          audioCaptureDefaults: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        }}
        onDisconnected={handleDisconnect}
      >
        <ConnectedView
          onDisconnect={handleDisconnect}
          isInDrawer={isInDrawer}
          isPreviewLink={isPreviewLink}
          previewAgent={previewAgent}
          onPreviewExitToSwitch={onPreviewExitToSwitch}
        />
        <RoomAudioRenderer />
      </LiveKitRoom>
    );
  }

  if (isPreviewLink) {
    const exit = () => onPreviewExitToSwitch?.();
    const backFromPhone = () => exit();

    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <PreviewCard sx={{ flex: 1 }}>
          <PreviewScreenHeader onBack={backFromPhone} />
          <Box sx={{ pt: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3.5 }}>
              <PreviewPhoneHero />
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 500, color: '#111', textAlign: 'center', mb: 1 }}>
              Enter Your Phone Number
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#6B7280', textAlign: 'center', mb: 2, lineHeight: 1.5 }}>
              We&apos;ll use this to provide better support and follow up if needed
            </Typography>
            <Box
              sx={{
                border: `1px solid ${PREVIEW_BLUE}`,
                borderRadius: 2,
                bgcolor: '#fff',
                overflow: 'hidden',
                '& .MuiOutlinedInput-root': {
                  minHeight: 48,
                  borderRadius: showPreviewSmsDisclaimer ? '8px 8px 0 0' : 2
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: 16,
                  lineHeight: '24px'
                }
              }}
            >
              <TelInput
                name='previewPhone'
                control={previewPhoneControl}
                onClick={() => setShowPreviewSmsDisclaimer((prev) => !prev)}
                defaultCountry='US'
                variant='outlined'
                placeholder='+16447205222'
              />
              {showPreviewSmsDisclaimer && <PreviewSmsDisclaimer embedded organizationName={organization?.name} />}
            </Box>
            <PreviewContinueButton disabled={!isValid} onClick={handleConnect} />
          </Box>
          <PreviewKickFooter organizationName={organization?.name} organizationLogoUrl={organization?.logo_url} />
        </PreviewCard>
        <PreviewFloatingClose onClick={exit} />
      </Box>
    );
  }

  return (
    <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' gap={2} px={3} py={5}>
      <TestAudioIcon />
      <Typography variant='h6' fontSize={16} fontWeight={600}>
        Enter your identity to continue
      </Typography>
      <MuiTelInput
        value={phone}
        onChange={(value, info) => {
          setPhone(value);
          setPhonePhoneOnly(info.nationalNumber ?? '');
          setCountryCode(`+${info.countryCallingCode}`);
        }}
        fullWidth
        onKeyDown={handleKeyDown}
        size='small'
        defaultCountry='US'
        variant='outlined'
        placeholder='+1(555) 000-0000'
      />
      <Button disabled={!isValid} onClick={handleConnect}>
        Continue
      </Button>
    </Box>
  );
}

function previewVoiceStatus(state: string): string {
  if (state === 'listening') return 'Listening...';
  if (state === 'speaking') return 'Speaking...';
  if (state === 'thinking') return 'Thinking...';
  if (state === 'connecting' || state === 'pre-connect-buffering' || state === 'initializing') return 'Connecting...';
  return 'Listening...';
}

function ConnectedView({
  onDisconnect,
  isInDrawer = false,
  isPreviewLink = false,
  previewAgent,
  onPreviewExitToSwitch
}: {
  onDisconnect: () => void;
  isInDrawer?: boolean;
  isPreviewLink?: boolean;
  previewAgent?: PublicAgentWidgetConfig;
  onPreviewExitToSwitch?: () => void;
}) {
  if (isPreviewLink) {
    return (
      <PreviewVoiceConnectedView
        onDisconnect={onDisconnect}
        previewAgent={previewAgent}
        onPreviewExitToSwitch={onPreviewExitToSwitch}
      />
    );
  }

  return <DefaultConnectedView onDisconnect={onDisconnect} isInDrawer={isInDrawer} />;
}

function PreviewVoiceConnectedView({
  onDisconnect,
  previewAgent,
  onPreviewExitToSwitch
}: {
  onDisconnect: () => void;
  previewAgent?: PublicAgentWidgetConfig;
  onPreviewExitToSwitch?: () => void;
}) {
  const { data: organization } = useGetOrganizations();
  const { state, audioTrack } = useVoiceAssistant();
  const [elapsed, setElapsed] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const t = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  const handleMuteChange = useCallback((enabled: boolean) => {
    setIsMuted(!enabled);
  }, []);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  const agentName = previewAgent?.name ?? 'Assistant';

  const backToSwitch = () => {
    onDisconnect();
    onPreviewExitToSwitch?.();
  };

  if (minimized) {
    return (
      <PreviewMinimizedVoiceStrip
        agentName={agentName}
        formattedTime={`${mm}:${ss}`}
        onExpand={() => setMinimized(false)}
        onEndCall={onDisconnect}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <PreviewCard sx={{ minHeight: 420 }}>
        <PreviewScreenHeader onBack={backToSwitch} />
        <Box
          sx={{ pt: 2, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111' }}>{agentName}</Typography>
          <Typography sx={{ fontSize: 16, color: '#374151', mt: 0.5 }}>
            {mm}:{ss}
          </Typography>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 140,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              my: 2
            }}
          >
            {audioTrack ? (
              <BarVisualizer
                state={state}
                trackRef={audioTrack}
                barCount={18}
                options={{ minHeight: 20, maxHeight: 90 }}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '100%'
                }}
              />
            ) : null}
            <Avatar
              src={previewAgent?.avatar_url ?? undefined}
              alt=''
              sx={{
                width: 88,
                height: 88,
                position: 'relative',
                zIndex: 1,
                border: '3px solid #fff',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
              }}
            >
              {agentName.slice(0, 1)}
            </Avatar>
          </Box>
          <Typography sx={{ fontSize: 16, color: '#111', mb: 2 }}>{previewVoiceStatus(state)}</Typography>
          <TrackToggle
            source={Track.Source.Microphone}
            onChange={handleMuteChange}
            style={{ background: 'transparent', border: 'none', padding: 0 }}
            showIcon={false}
          >
            <Box
              component='span'
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: PREVIEW_BLUE,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(37,99,235,0.35)'
              }}
            >
              <MdMic size={28} style={{ opacity: isMuted ? 0.5 : 1 }} />
            </Box>
          </TrackToggle>
        </Box>
        <PreviewKickFooter organizationName={organization?.name} organizationLogoUrl={organization?.logo_url} />
      </PreviewCard>
      <PreviewFloatingVoiceBar onMinimize={() => setMinimized(true)} onEndCall={onDisconnect} />
    </Box>
  );
}

function DefaultConnectedView({
  onDisconnect,
  isInDrawer = false
}: {
  onDisconnect: () => void;
  isInDrawer?: boolean;
}) {
  const theme = useTheme();
  const transcriptions = useTranscriptions();
  const [isMuted, setIsMuted] = useState(false);

  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcriptions]);

  const handleMuteChange = useCallback((enabled: boolean) => {
    setIsMuted(!enabled);
  }, []);

  return (
    <Box
      display='flex'
      flexDirection='column'
      sx={{
        width: '100%',
        height: isInDrawer ? '100%' : '100%',
        minHeight: isInDrawer ? 'calc(100vh - 180px)' : 0,
        overflow: 'hidden'
      }}
    >
      <Paper
        elevation={0}
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          borderRadius: 2,
          p: 1,
          mx: 1,
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minHeight: 0
        }}
      >
        {transcriptions && transcriptions.length > 0 ? (
          transcriptions.map((transcription, index) => {
            const isHuman = transcription.participantInfo?.identity === 'human';
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: isHuman ? 'flex-end' : 'flex-start',
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    maxWidth: '75%',
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: isHuman ? theme.palette.grey[200] : theme.palette.primary.light,
                    color: theme.palette.text.primary,
                    wordBreak: 'break-word'
                  }}
                >
                  <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                    {transcription.text}
                  </Typography>
                </Box>
              </Box>
            );
          })
        ) : (
          <Typography variant='body2' color='text.secondary' textAlign='center' sx={{ mt: 4 }}>
            Waiting for transcription...
          </Typography>
        )}
      </Paper>

      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        justifyContent='center'
        gap={2}
        px={2}
        py={1.5}
        width='100%'
        sx={{ boxSizing: 'border-box' }}
      >
        <TrackToggle
          source={Track.Source.Microphone}
          onChange={handleMuteChange}
          style={{ background: 'transparent', border: 'none' }}
          showIcon={false}
        >
          <Button
            variant='outlined'
            sx={{
              px: 3,
              borderRadius: '9999px',
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: theme.palette.primary.light
              }
            }}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </Button>
        </TrackToggle>

        <Button
          variant='outlined'
          onClick={onDisconnect}
          sx={{
            px: 5,
            borderRadius: '9999px',
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            '&:hover': {
              borderColor: theme.palette.error.dark,
              backgroundColor: theme.palette.error.light
            }
          }}
        >
          End Call
        </Button>
      </Box>
    </Box>
  );
}
